import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import Tooltip from '@mui/material/Tooltip';

const apiUrl = process.env.REACT_APP_API_URL;

const MedicationsList = () => {
  const { t } = useTranslation();
  const [medications, setMedications] = useState([]);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMedicationId, setSelectedMedicationId] = useState(null);
  const [stockQty, setStockQty] = useState('');
  const [disposalDialogOpen, setDisposalDialogOpen] = useState(false);
  const [disposalQty, setDisposalQty] = useState('');

  useEffect(() => {
    fetchMedications();
  }, []);
  const openStockDialog = (medication_id) => {
    setSelectedMedicationId(medication_id);
    setStockQty('');
    setOpenDialog(true);
  };

  const fetchMedications = async () => {
    try {
      const response = await axios.get(`${apiUrl}/medications`);
      setMedications(response.data);
    } catch (error) {
      console.error('Error fetching medications:', error);
    }
  };

  const handleDelete = (medicationId) => {
    if (window.confirm(t('medicationsList.confirmDelete'))) {
      const userId = localStorage.getItem('userId'); // ← تأكد أن `userId` محفوظ بعد تسجيل الدخول

      if (!userId) {
        setSnackbar({
          open: true,
          message: t('medicationsList.userNotFound'),
          severity: 'error',
        });
        return;
      }

      axios
        .post(`${apiUrl}/medications/delete/${medicationId}`, {
          user_id: parseInt(userId),
        })
        .then(() => {
          fetchMedications();
          setSnackbar({
            open: true,
            message: t('medicationsList.deleteSuccess'),
            severity: 'success',
          });
        })
        .catch((error) => {
          console.error('Error deleting medication:', error);
          setSnackbar({
            open: true,
            message: t('medicationsList.deleteError'),
            severity: 'error',
          });
        });
    }
  };

  const handleStockSubmit = async () => {
    const parsedQty = parseFloat(stockQty);
    const user_id = parseInt(localStorage.getItem('userId'));
    if (isNaN(parsedQty) || parsedQty <= 0) {
      setSnackbar({
        open: true,
        message: t('medicationsList.invalidQuantity'),
        severity: 'warning',
      });
      return;
    }

    try {
      await axios.post(`${apiUrl}/medications/add-stock`, {
        medication_id: selectedMedicationId,
        quantity: parsedQty,
        user_id,
      });
      setOpenDialog(false);
      await fetchMedications();
      setSnackbar({
        open: true,
        message: t('medicationsList.stockAddedSuccess'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Error adding stock:', err);
      setSnackbar({
        open: true,
        message: t('medicationsList.stockAddError'),
        severity: 'error',
      });
    }
  };

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const handleDisposeStock = () => {
    const qty = parseFloat(disposalQty);
    const user_id = parseInt(localStorage.getItem('userId'));

    if (!qty || qty <= 0 || isNaN(qty)) {
      setSnackbar({
        open: true,
        message: t('medicationsList.invalidQuantity'),
        severity: 'warning',
      });
      return;
    }

    axios
      .post(`${apiUrl}/medications/dispose-stock`, {
        medication_id: selectedMedicationId,
        quantity: qty,
        user_id,
      })
      .then(() => {
        setSnackbar({
          open: true,
          message: t('medicationsList.stockDisposedSuccess'),
          severity: 'success',
        });
        fetchMedications();
        setDisposalDialogOpen(false);
        setDisposalQty('');
      })
      .catch((error) => {
        console.error('Error disposing stock:', error);
        setSnackbar({
          open: true,
          message: t('medicationsList.stockDisposeError'),
          severity: 'error',
        });
      });
  };

  return (
    <Container component="main" maxWidth="lg">
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>{t('medicationsList.enterStockAmount')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            type="number"
            margin="dense"
            fullWidth
            value={stockQty}
            onChange={(e) => setStockQty(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('cancel')}</Button>
          <Button onClick={handleStockSubmit}>{t('confirm')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={disposalDialogOpen}
        onClose={() => setDisposalDialogOpen(false)}
      >
        <DialogTitle>{t('medicationsList.disposeStock')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('medicationsList.enterDisposalAmount')}
            fullWidth
            type="number"
            value={disposalQty}
            onChange={(e) => setDisposalQty(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisposalDialogOpen(false)}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDisposeStock} color="error">
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          marginTop: 2.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4" sx={{ mb: 2.5 }}>
          {t('medicationsList.title')}
        </Typography>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            mb: 1,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/medication-add')}
            style={{ marginTop: 0, alignSelf: 'center' }}
          >
            {t('medicationsList.addNew')}
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ marginTop: 2.5 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">#</TableCell>
                <TableCell align="center">
                  {t('medicationsList.name')}
                </TableCell>
                <TableCell align="center">
                  {t('medicationsList.defaultDosage')}
                </TableCell>
                <TableCell align="center">
                  {t('medicationsList.stock')}
                </TableCell>

                <TableCell align="center">
                  {t('medicationsList.defaultUnit')}
                </TableCell>
                <TableCell align="center">
                  {t('medicationsList.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {medications.length > 0 ? (
                medications.map((medication, index) => (
                  <TableRow key={medication.medication_id}>
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell align="center">
                      {medication.medication_name}
                    </TableCell>
                    <TableCell align="center">
                      {medication.default_dosage}
                    </TableCell>
                    <TableCell align="center">
                      {medication.stock_quantity}
                    </TableCell>

                    <TableCell align="center">
                      {medication.default_unit}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() =>
                          navigate(
                            `/medication-edit/${medication.medication_id}`
                          )
                        }
                      >
                        <EditIcon />
                      </IconButton>
                      <Tooltip title={t('medicationsList.addtostock')}>
                        <IconButton
                          color="success"
                          onClick={() =>
                            openStockDialog(medication.medication_id)
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('medicationsList.dispose')}>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setSelectedMedicationId(medication.medication_id);
                            setDisposalDialogOpen(true);
                          }}
                        >
                          <DeleteSweepIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete')}>
                        <IconButton
                          color="secondary"
                          onClick={() => handleDelete(medication.medication_id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {t('medicationsList.noMedications')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MedicationsList;
